import { Button } from '@ui-components/neo.tax';
import { Link } from 'react-router-dom';

import {
  AnalysisType,
  DocumentStatus,
  EMAIL_TO_SUPPORT_URL,
  getPath,
  Path,
  PageLayoutProps,
  useBackgroundOperations,
  useCurrentUserScopeForCurrentCompany,
  useUnfinishedSyncEventInPeriodByAnalysisType,
  useGetFederalDocumentStatuses,
  useCurrentCompanyId,
} from '@neo.tax/shared';
import { BackgroundOperationName } from '@neo.tax/shared/graphql';

import { useRdContext } from '../context';
import { useGetStateDocumentStatus } from './useGetStateDocumentStatus';

type PageLockStatus = {
  loading: boolean;
  pageLockStatus: Pick<PageLayoutProps, 'locked' | 'lockButtons' | 'lockedReason'>;
};

export const useRdPageLockStatus = ({
  disableIfOngoingSync,
  disableIfProjectDetailsUpdateInProgress,
  disableIfRdCostPendingValidation,
  disableIfRdProjectsImportInProgress,
  disableIfSupervisorAllocationInProgress,
}: {
  disableIfOngoingSync?: boolean;
  disableIfProjectDetailsUpdateInProgress?: boolean;
  disableIfRdCostPendingValidation?: boolean;
  disableIfRdProjectsImportInProgress?: boolean;
  disableIfSupervisorAllocationInProgress?: boolean;
} = {}): PageLockStatus => {
  const companyId = useCurrentCompanyId();
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const { fiscalYear, fiscalYearId, periodName } = useRdContext();
  const { documentStatuses: federalDocumentStatuses, loading: loadingFederalDocuments } = useGetFederalDocumentStatuses(
    {
      skip: limitAccessToFinancialData,
      fiscalYearId,
    },
  );
  const { loading: loadingStateDocuments, stateDocumentStatus } = useGetStateDocumentStatus({
    skip: limitAccessToFinancialData,
    fiscalYearId,
  });
  const { loading: loadingUnfinishedSyncEventsPeriod, period: unfinishedSyncEventsPeriod } =
    useUnfinishedSyncEventInPeriodByAnalysisType({ fiscalYearId, periodName, analysisType: AnalysisType.Rd });
  const { backgroundOperations, loading: loadingBackgroundOperations } = useBackgroundOperations();

  if (
    loadingFederalDocuments ||
    loadingStateDocuments ||
    loadingUnfinishedSyncEventsPeriod ||
    ((disableIfSupervisorAllocationInProgress ||
      disableIfRdProjectsImportInProgress ||
      disableIfProjectDetailsUpdateInProgress) &&
      loadingBackgroundOperations)
  ) {
    return {
      loading: true,
      pageLockStatus: {
        locked: false,
      },
    };
  }

  // Lock if supervisor allocation is in progress
  const hasSupervisorAllocationInProgress =
    backgroundOperations?.some(
      (op) => op.name === BackgroundOperationName.ApplyRdSupervisorAllocationOperation && op.progressPercent < 100,
    ) ?? false;
  if (disableIfSupervisorAllocationInProgress && hasSupervisorAllocationInProgress) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason: 'Supervisor R&D allocation is being applied. Please wait for it to complete.',
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  // Lock while an R&D projects CSV import is being processed in the background.
  const hasRdProjectsImportInProgress =
    backgroundOperations?.some(
      (op) => op.name === BackgroundOperationName.ImportRdProjectsFileOperation && op.progressPercent < 100,
    ) ?? false;
  if (disableIfRdProjectsImportInProgress && hasRdProjectsImportInProgress) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason:
          "We're creating your projects from the uploaded CSV. This usually takes a few minutes. When it's done, they'll load automatically on this page.",
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  // Lock while an "Update Project Details" run is rewriting this fiscal year's projects.
  const hasProjectDetailsUpdateInProgress =
    backgroundOperations?.some(
      (op) => op.name === BackgroundOperationName.RegenerateRdProjectDetailsOperation && op.progressPercent < 100,
    ) ?? false;
  if (disableIfProjectDetailsUpdateInProgress && hasProjectDetailsUpdateInProgress) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason:
          "We're updating project details in the background. The projects will be editable again when the job finishes.",
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  // Lock if there are R&D cost uploads with pending validation
  const hasRdCostPendingValidation =
    fiscalYear.syncedRdPayrollCost.some(
      (syncedRdPayrollCost) => syncedRdPayrollCost.fileUpload && syncedRdPayrollCost.fileUpload.isValid === null,
    ) ?? false;
  if (disableIfRdCostPendingValidation && hasRdCostPendingValidation) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason:
          "We're processing your payroll cost file. This usually takes a few minutes. When it's done, the data will load automatically on this page.",
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  if (disableIfOngoingSync && unfinishedSyncEventsPeriod) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason: 'Project Analysis is in progress. Please check back later.',
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  const isFederalDocumentReadyForDownload =
    federalDocumentStatuses?.taxCreditFormStatus === DocumentStatus.ReadyForDownload;

  const isStateDocumentReadyForDownload = stateDocumentStatus === true;

  if (!fiscalYear.lockedForCustomers) {
    return {
      loading: false,
      pageLockStatus: {
        locked: false,
      },
    };
  }

  if (limitAccessToFinancialData) {
    // non financial users don't have access to tax documents page
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason: 'It has been submitted for review. Please contact Neo.Tax admin if any changes need to be made.',
        lockButtons: (
          <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
            Contact Support
          </Button>
        ),
      },
    };
  }

  if (isFederalDocumentReadyForDownload || isStateDocumentReadyForDownload) {
    return {
      loading: false,
      pageLockStatus: {
        locked: true,
        lockedReason: 'Documents are ready to download, please navigate to Tax Documents page.',
        lockButtons: (
          <Button
            size="xs"
            variant="white"
            rounded="regular"
            as={Link}
            to={getPath({ path: Path.EXPORT_TAX, companyId, fiscalYearId })}
          >
            Go To Tax Documents
          </Button>
        ),
      },
    };
  }

  return {
    loading: false,
    pageLockStatus: {
      locked: true,
      lockedReason: 'Review is in progress. Please contact Neo.Tax admin if any changes need to be made.',
      lockButtons: (
        <Button size="xs" variant="white" rounded="regular" as="a" href={EMAIL_TO_SUPPORT_URL} target="_blank">
          Contact Support
        </Button>
      ),
    },
  };
};
